/**
 * Commentary toolbar — the actions for the whole AI view, sitting above
 * In Context rather than trailing the response (Brian, UAT 2026-09-21).
 *
 * Print renders the same page the study lesson binds as its commentary
 * sheet, so a printout from here and one from a study are the same page.
 * Report keeps its own dialog; it just moved up beside print.
 */

import { useState } from 'react';
import { PrintIcon } from '@components/icons';
import { bibleState } from '@/state/bible-state';
import { ReportButton } from './report-button';
import type { ReportInput } from './build-report-markdown';

interface CommentaryToolbarProps {
  /** The finished commentary this toolbar acts on. Absent → nothing to do. */
  commentary?: string;
  reference?: string;
  pericopeTitle?: string;
  reportInput: ReportInput | null;
  originalQuery?: string;
}

export function CommentaryToolbar({
  commentary,
  reference,
  pericopeTitle,
  reportInput,
  originalQuery,
}: CommentaryToolbarProps) {
  const [printing, setPrinting] = useState(false);
  if (!commentary || !reference) return null;

  const handlePrint = async () => {
    setPrinting(true);
    try {
      // Lazy: jspdf is a ~350 kB chunk the reader only pays for on print.
      const { generateCommentaryPdf } = await import('@/services/study-pdf');
      const blob = await generateCommentaryPdf({
        pericopeTitle: pericopeTitle ?? '',
        reference,
        text: commentary,
        generatedAt: new Date().toISOString(),
      });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) printWindow.onload = () => printWindow.print();
      else bibleState.showToast('Allow pop-ups to print the commentary.', 'error');
    } catch (error) {
      console.error('Commentary print failed:', error);
      bibleState.showToast('Failed to generate the commentary for printing.', 'error');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="commentary-toolbar" data-testid="commentary-toolbar">
      <button
        type="button"
        className="commentary-toolbar-button"
        onClick={() => void handlePrint()}
        disabled={printing}
        title="Print this commentary"
        data-testid="commentary-print"
      >
        <PrintIcon className="h-4 w-4" />
      </button>
      {reportInput && <ReportButton input={reportInput} originalQuery={originalQuery} />}
    </div>
  );
}
